import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';
import { getCategory, formatDate, formatViews } from '@/utils/content';

type ArticleCardProps = {
  article: Article;
  variant?: 'default' | 'wide' | 'compact';
  index?: number;
};

export default function ArticleCard({ article, variant = 'default', index }: ArticleCardProps) {
  const category = getCategory(article.categoryId);
  const href = `/article/${article.id}`;

  if (variant === 'compact') {
    return (
      <article className="group flex items-start gap-4 border-b border-background-200 py-4 last:border-b-0">
        <span className="font-heading text-2xl font-bold leading-none text-primary-400 md:text-3xl">
          {typeof index === 'number' ? String(index + 1).padStart(2, '0') : '·'}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-heading text-base font-semibold leading-snug text-foreground-950">
            <Link to={href} className="cursor-pointer transition-colors hover:text-primary-700">
              {article.title}
            </Link>
          </h3>
          <p className="mt-1.5 line-clamp-2 text-sm text-foreground-600">{article.excerpt}</p>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
            <span className="whitespace-nowrap">{category?.name}</span>
            <span className="whitespace-nowrap">{formatDate(article.date)}</span>
            <span className="flex items-center gap-1 whitespace-nowrap">
              <i className="ri-eye-line"></i>
              {formatViews(article.views)}
            </span>
          </div>
        </div>
      </article>
    );
  }

  if (variant === 'wide') {
    return (
      <article className="group overflow-hidden rounded-lg border border-background-200 bg-background-100/70">
        <div className="flex flex-col md:flex-row">
          <Link to={href} className="relative block h-56 w-full shrink-0 cursor-pointer overflow-hidden md:h-auto md:w-72 lg:w-96">
            <img
              src={article.cover}
              alt={article.coverAlt}
              className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            />
          </Link>
          <div className="flex flex-1 flex-col justify-center p-5 md:p-7">
            <div className="flex flex-wrap items-center gap-2">
              <span className="whitespace-nowrap rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900">
                {category?.name}
              </span>
              {article.tags.slice(0, 2).map((tag) => (
                <span key={tag} className="whitespace-nowrap rounded-full border border-background-300 px-2.5 py-1 text-xs text-foreground-600">
                  {tag}
                </span>
              ))}
            </div>
            <h3 className="mt-3 font-heading text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
              <Link to={href} className="cursor-pointer transition-colors hover:text-primary-700">
                {article.title}
              </Link>
            </h3>
            <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-foreground-600">{article.excerpt}</p>
            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="whitespace-nowrap">{formatDate(article.date)}</span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-time-line"></i>
                {article.readTime} 分钟
              </span>
            </div>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-100/60">
      <Link to={href} className="relative block h-48 w-full cursor-pointer overflow-hidden">
        <img
          src={article.cover}
          alt={article.coverAlt}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 whitespace-nowrap rounded-full bg-background-50/90 px-2.5 py-1 text-xs text-foreground-800">
          {category?.name}
        </span>
      </Link>
      <div className="flex flex-1 flex-col p-5">
        <h3 className="font-heading text-lg font-semibold leading-snug text-foreground-950">
          <Link to={href} className="cursor-pointer transition-colors hover:text-primary-700">
            {article.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{article.excerpt}</p>
        <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500 md:mt-auto md:pt-4">
          <span className="whitespace-nowrap">{formatDate(article.date)}</span>
          <span className="whitespace-nowrap">{article.readTime} 分钟阅读</span>
          <span className="flex items-center gap-1 whitespace-nowrap">
            <i className="ri-eye-line"></i>
            {formatViews(article.views)}
          </span>
        </div>
      </div>
    </article>
  );
}